import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { CdkListbox, CdkOption } from '@angular/cdk/listbox';

import { IconComponent } from '../../../../shared/icon/icon.component';
import type { Size } from '../../../orders/order.model';
import { formatSize, getSizeKey, type SizeKey } from '../../../orders/size.utils';
import { formatMetricValue } from '../../statistics/statistics-metrics';
import { FilterDropdown } from '../../../../shared/directives/filter-dropdown';
import { SizeGlyphComponent } from './size-glyph.component';

type SizeFilter = SizeKey | 'all';

interface SizeOption {
  readonly key: SizeFilter;
  readonly label: string;
  readonly price?: string;
  readonly mostPopular: boolean;
  // `null` is the "All sizes" entry.
  readonly size: Size | null;
}

@Component({
  selector: 'app-size-select',
  imports: [CdkListbox, CdkOption, IconComponent, SizeGlyphComponent],
  templateUrl: './size-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'relative' },
  hostDirectives: [{ directive: FilterDropdown, outputs: ['opened'] }],
})
export class SizeSelectComponent {
  protected readonly dropdown = inject(FilterDropdown);

  // Expected to be sorted already (by area); rendered in the given order.
  readonly sizes = input<readonly Size[]>([]);
  readonly value = model<SizeFilter>('all');

  protected readonly options = computed<readonly SizeOption[]>(() => [
    { key: 'all', label: 'All sizes', mostPopular: false, size: null },
    ...this.sizes().map(size => ({
      key: getSizeKey(size),
      label: formatSize(size),
      price: formatMetricValue('revenue', size.price),
      mostPopular: size.mostPopular,
      size,
    })),
  ]);

  protected readonly selected = computed(
    () => this.options().find(option => option.key === this.value()) ?? this.options()[0]
  );
}
